import { FC, useState } from "react";

import { AddCourseModal } from "../AddCourseModal";

import { LoadingScreen } from "components/LoadingScreen";
import { EditableField } from "components/hover-widgets/EditableField";
import { AdminCourseListPanel } from "components/materials/home/admin/content/AdminCourseList";
import { TermOptionsMenu } from "components/menus/TermOptionsMenu";
import { TermType } from "components/server-types";
import { t } from "i18n/i18n";
import { AccordionTitle } from "mds/components/AccordionTitle";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import { AddPlusIcon, CalendarIcon, EyeNotVisibleIcon, FolderIcon } from "mds/icons";
import { useAppDispatch } from "store/index";
import { selectGroupingsWithCourseCodesByTerm } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { onTitleChanged } from "utils/store-utils";

type CourseDetailCourseCodeViewProps = {
  term: TermType;
  eventPrefix?: string;
  canEdit: boolean;
};

export const CourseDetailCourseCodeView: FC<CourseDetailCourseCodeViewProps> = ({
  term,
  eventPrefix,
  canEdit,
}) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const groupedCourseCodes = useListSelector((s) =>
    selectGroupingsWithCourseCodesByTerm(s, term?.id),
  );

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  if (!term) {
    return <LoadingScreen>{t("home.settings.no_terms")}</LoadingScreen>;
  }

  const showAddCourseModal = () => {
    showConfirmationModal({
      title: t("home.add_course_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseModal
          defaultTermId={term.id}
          eventName={eventPrefix ? `${eventPrefix}Add Course` : undefined}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <div className="course-detail-page px-0 py-3 text-black-tint-20 md:px-7">
      <h2 className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {term.archived_at ? (
            <EyeNotVisibleIcon className="text-blue" size="l" />
          ) : (
            <CalendarIcon className="text-blue" size="l" />
          )}

          <div className="flex items-center">
            <EditableField
              isEditing={isEditing}
              label={t("glossary.term_title")}
              textSize="h2"
              value={
                term.archived_at && !isEditing
                  ? t("home.terms.archived_term_format", { term: term.title })
                  : term.title
              }
              onSetIsEditing={setIsEditing}
              onValueChanged={(newTitle) => onTitleChanged("terms", term.id, newTitle)}
            />
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <TermOptionsMenu
              eventPrefix={eventPrefix}
              term={term}
              onRename={() => setIsEditing(true)}
            />

            <Button
              eventName={eventPrefix ? `${eventPrefix}Add Course` : undefined}
              kind="gray"
              size="s"
              onClick={showAddCourseModal}
            >
              <IconText iconStart={<AddPlusIcon />} text={t("common.add.course")} />
            </Button>
          </div>
        )}
      </h2>

      {!groupedCourseCodes.length ? (
        <Banner
          buttonChildren={
            canEdit ? (
              <Button
                eventName={eventPrefix ? `${eventPrefix}Add Course` : undefined}
                kind="blue"
                size="s"
                onClick={showAddCourseModal}
              >
                <IconText iconStart={<AddPlusIcon />} text={t("common.add.course")} />
              </Button>
            ) : (
              <Button
                eventName={eventPrefix ? `${eventPrefix}Enable Edit Mode` : undefined}
                kind="blue"
                size="s"
                onClick={handleEnableEditMode}
              >
                {t("warning.enable_edit_mode")}
              </Button>
            )
          }
          kind="info"
        >
          {canEdit
            ? t("home.terms.empty_term_detail_banner_text")
            : t("home.terms.empty_term_detail_banner_text_no_edit")}
        </Banner>
      ) : (
        groupedCourseCodes.map((groupingWithCodes, index) => (
          <div key={groupingWithCodes.id || index}>
            {groupingWithCodes.id ? (
              <AccordionTitle
                headerClassName="ml-2 w-[calc(100%-12px)] justify-between border-b border-black-tint-90 pr-4 mb-3"
                iconDirection="end"
                title={
                  <div className="flex items-center gap-2">
                    <FolderIcon className="text-blue" size="m" />
                    <span className="body-m font-medium">{groupingWithCodes.title}</span>
                  </div>
                }
                startOpen
              >
                <ul>
                  {groupingWithCodes.courseCodes.map((courseCode) => (
                    <li className="course-panel ml-8" key={courseCode.id}>
                      <AdminCourseListPanel
                        canEdit={canEdit}
                        courses={courseCode.courses}
                        eventPrefix={eventPrefix}
                        eventProperties={{ courseCode: courseCode.title, courseTerm: term.title }}
                        title={courseCode.title}
                      />
                    </li>
                  ))}
                </ul>
              </AccordionTitle>
            ) : (
              <ul>
                {groupingWithCodes.courseCodes.map((courseCode) => (
                  <li className="course-panel ml-2" key={courseCode.id}>
                    <AdminCourseListPanel
                      canEdit={canEdit}
                      courses={courseCode.courses}
                      eventPrefix={eventPrefix}
                      eventProperties={{ courseCode: courseCode.title, courseTerm: term.title }}
                      title={courseCode.title}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))
      )}
    </div>
  );
};
